<script>
import 'mdn-polyfills/Array.prototype.findIndex';
import debounce from 'lodash/debounce';
import deburr from 'lodash/deburr';
import enterView from 'enter-view';

const ALPHABET = [...'#abcdefghijklmnopqrstuvwxyz'];

export default {
  name: 'AlphabeticalList',

  filters: {
    mark: (str, query) => {
      if (query.length) {
        const originalValue = str.toString();
        const originalValueNoDiacritics = deburr(originalValue.toLowerCase());
        const queryNoDiacritics = deburr(query.toLowerCase());

        const indexOfFirstHit = originalValueNoDiacritics.indexOf(queryNoDiacritics);
        if (indexOfFirstHit >= 0) {
          const originalTerm = originalValue.substring(indexOfFirstHit, indexOfFirstHit + query.length);
          const regex = new RegExp(originalTerm, 'ig');
          return originalValue.replace(regex, (res) => `<mark>${res}</mark>`);
        }
      }
      return str.toString();
    },
  },

  props: {
    items: {
      type: Array,
      required: true,
    },
    isSearchEnabled: {
      type: Boolean,
      default: false,
    },
    noResultsFormat: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      active: null,
      focusLetter: null,
      popup: null,
      search: '',
      target: null,
      activeNav: false,
      isMobile: false,
      toShowResults: false
    };
  },

  created() {
    window.addEventListener('resize', debounce(this.resizeHandler, 250));
    window.addEventListener('scroll', this.scrollHandler);
  },

  mounted() {
    this.setVars();

    this.stickyScroller = enterView({
      offset: 1,
      selector: '.js-sticky',
      enter: (e) => e.classList.add('has-reached-top'),
      exit: (e) => {
        e.classList.remove('has-reached-top');
        this.focusLetter = null;
      },
    });
  },

  methods: {
    resizeHandler() {
      this.setVars();
    },

    scrollHandler() {
      document.querySelectorAll('.js-letters-group').forEach(letter => {
        let letterTop = letter.getBoundingClientRect().top - 10;
        if (letterTop < (this.$refs.searchstretchbar.getBoundingClientRect().bottom)) {
          this.focusLetter = letter.getAttribute('data-name');
          return false;
        }
      })
    },

    setVars() {
      this.isMobile = window.innerWidth < 768;

      this.headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
      this.navHeight = 0;
      this.searchHeight = 0;

      if (this.$refs.navstretchbar && this.$refs.searchstretchbar) {
        this.navHeight = this.isMobile ? 0 : this.$refs.navstretchbar.getBoundingClientRect().height;
        this.searchHeight = this.$refs.searchstretchbar.getBoundingClientRect().height;
      }

      const max = document.body.clientWidth;
      document.documentElement.style.setProperty('--vw', `${max}px`);
    },

    showPopup(item) {
      this.popup = {
        title: item.popupTitle,
        desc: item.popupDesc,
        options: item.links,
      };

      this.$nextTick(() => this.$refs.popupOptions[0].focus());
    },

    closePopup() {
      this.popup = null;
      this.active = null;
      this.$nextTick(() => this.target.focus());
    },

    handleSearchEnter() {
      const resultRect = this.$el.querySelector('.alphabetical-list__results').getBoundingClientRect();
      const posY = resultRect.top + window.pageYOffset;
      this.smoothScroll(posY);
      this.toShowResults = true;
    },

    handleSearchFocus() {
      if (this.$el.classList.contains('has-reached-top')) return;
      const firstLetter = document.querySelector('.alphabetical-list__nav__item.has-results a');
      if (!firstLetter) return;

      const letter = firstLetter.innerText.toLowerCase();
      this.setLetterFocus(letter);
    },

    setActive(e, item) {
      this.target = e.currentTarget;
      this.active = item;
    },

    setLetterFocus(letter) {
      this.focusLetter = letter;
      const target = document.querySelector(`[id="${letter}"], [id="${letter.toUpperCase()}"]`);

      const posY = Math.round(target.parentElement.getBoundingClientRect().top) + window.pageYOffset;
      this.smoothScroll(posY);

      if (document.querySelector('html').classList.contains('keyboard-user')) {
        target.focus();
      }
    },

    smoothScroll(posY) {
      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        let destY = Math.ceil(posY - this.navHeight - this.searchHeight);
        if (window.scrollY > destY) destY -= this.headerHeight;

        window.scrollTo({
          behavior: 'smooth',
          top: destY,
        });
      }, 200);
    },

    focusFirstPopupOption(e) {
      e.preventDefault();
      this.$refs.popupOptions[0].focus();
    },

    focusPopupClose(e) {
      if (e.target === this.$refs.popupOptions[0]) {
        e.preventDefault();
        this.$refs.popupClose.focus();
      }
    },

    handleKeyUp() {
      if (this.filteredItems.length === 0) {
        this.smoothScroll(0);
      } else {
        this.handleSearchEnter();
      }
    },

    // touch / mobile

    getClosestElement(x, y) {
      const distances = [];

      const elements = Array.from(this.$el.querySelectorAll('.alphabetical-list__nav__item.has-results'));

      const coordinates = elements.map((element) => {
        const rect = element.getBoundingClientRect();
        return [rect.x + element.offsetWidth / 2, rect.y + element.offsetHeight / 2];
      });

      coordinates.forEach((coordinate) => {
        const distance = Math.hypot(coordinate[0] - parseInt(x, 10), coordinate[1] - parseInt(y, 10));
        distances.push(parseInt(distance, 10));
      });

      const closestLinkIndex = distances.indexOf(Math.min(...distances));

      return elements[closestLinkIndex];
    },

    scrollToClosestTouchedLetter(e) {
      const { clientX, clientY } = e.touches[0];
      const newActiveElement = this.getClosestElement(clientX, clientY);
      this.setLetterFocus(newActiveElement.dataset.letter);
    },

    onNavTouchstart(e) {
      if (!this.isMobile) return;
      this.activeNav = true;
      this.scrollToClosestTouchedLetter(e);
    },

    // eslint-disable-next-line func-names
    onNavTouchmove: debounce(function (e) {
      if (!this.isMobile) return;
      this.scrollToClosestTouchedLetter(e);
    }, 10),

    onNavTouchend() {
      if (!this.isMobile) return;
      this.activeNav = false;
    },
  },

  watch: {
    active: {
      handler(val) {
        if (val && val.links && val.links.length === 1) {
          window.location = val.links[0].url;
        } else if (val != null) {
          this.showPopup(val);
        }
      },
    },
  },

  computed: {
    filteredItems() {
      if (this.search.length >= 1) {
        this.toShowResults = true;
        const searchTerm = deburr(this.search.toLowerCase());
        return this.items.filter((item) => {
          let t = false;
          let d = false;

          // if item has a title, check if it contains the search term
          if (item.title) {
            t = deburr(item.title.toLowerCase()).includes(searchTerm);
          }

          // if item has a description, check if it contains the search term
          if (item.desc) {
            d = deburr(item.desc.toLowerCase()).includes(searchTerm);
          }

          return t || d;
        });
      }
      else {
        this.toShowResults = false;
      }

      return this.items;
    },
    activeAlphabet() {
      return ALPHABET.filter((character) => this.filteredItems.some((item) => {
        const titleFirstCharacter = deburr(item.title[0].toLowerCase())[0];
        // if character is `#`, test if title first character is a number
        // else character is a letter of alphabet, test if title first character match the current character
        return (character === '#') ? !Number.isNaN(Number(titleFirstCharacter)) : titleFirstCharacter === character;
      }));
    },
    navItems() {
      return ALPHABET.map((character) => ({
        label: character,
        hasResults: this.activeAlphabet.includes(character.toLowerCase()),
      }));
    },
    orderedItems() {
      const { filteredItems } = this;
      filteredItems.sort((a, b) => (deburr(a.title.toLowerCase()) > deburr(b.title.toLowerCase()) ? 1 : -1));

      return this.activeAlphabet.map((character) => {
        const items = filteredItems.filter((item) => {
          const titleFirstCharacter = deburr(item.title[0].toLowerCase())[0];
          return (character === '#') ? !Number.isNaN(Number(titleFirstCharacter)) : titleFirstCharacter === character;
        });
        return { name: character, items };
      });
    },
    noResultsMessage() {
      return this.noResultsFormat.replace('{0}', this.search);
    },
  },
};
</script>
